/**
 * Admin SPA root router.
 *
 * Top-level admin sections live at the subdomain root (admin.vehix.ai).
 * Every route is gated by AdminProtectedRoute, which consumes the
 * adminRbacMatrix (currently SUPER_ADMIN-only across the board).
 *
 * Sidebar nav groups map to URL prefixes:
 *   /setup/*    → Tenant Setup, User Setup, Zone Setup
 *   /workflow/* → Workflow Management, Pipelines
 *   /analytics  → User Analytics (renamed from "Analytics")
 *   /feedback   → Feedback inbox
 *
 * /api-docs is reachable from the footer link only — no sidebar entry.
 *
 * Auth callback routes mirror portal's set so the cookie SSO flow can land
 * users on admin.vehix.ai during password reset / invite / OAuth.
 *
 * Legacy redirects preserve bookmarks for the old Control Center tab URLs
 * and the pre-restructure /pipelines paths.
 */

import React, { Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation
} from 'react-router-dom';

/**
 * Redirect for the legacy `/pipelines/:id` URL pattern. Preserves the `:id`
 * segment when forwarding to the canonical `/workflow/pipelines/:id`
 * detail route — the previous static `<Navigate to='/workflow/pipelines'>`
 * dropped the id, which silently sent every detail-page click to the list
 * view instead.
 *
 * The query string is carried across too: the runs list and detail page keep
 * their filters there, so dropping it would silently reset a shared link.
 */
const LegacyPipelineDetailRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const base = id ? `/workflow/pipelines/${id}` : '/workflow/pipelines';
  return <Navigate to={`${base}${search}`} replace />;
};

/** Legacy `/pipelines` list URL. Keeps the filter query string intact. */
const LegacyPipelineListRedirect: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={`/workflow/pipelines${search}`} replace />;
};
import { Loader2 } from 'lucide-react';
import { EnhancedAuthProvider } from '@vehix/portal/features/auth/contexts/EnhancedAuthContext';
import { ThemeProvider } from '@vehix/portal/contexts/ThemeContext';
import { TenantProvider } from '@vehix/portal/contexts/TenantContext';

// Auth callback pages — reused from portal.
import { AuthPage } from '@vehix/portal/features/auth/pages/AuthPage';
import { AuthCallback } from '@vehix/portal/features/auth/components/AuthCallback';
import { SetPasswordPage } from '@vehix/portal/features/auth/pages/SetPasswordPage';
import { ResetPasswordPage } from '@vehix/portal/features/auth/pages/ResetPasswordPage';
import { ResetPasswordConfirmPage } from '@vehix/portal/features/auth/pages/ResetPasswordConfirmPage';
import { InvitePage } from '@vehix/portal/features/auth/pages/InvitePage';
import { InviteConfirmPage } from '@vehix/portal/features/auth/pages/InviteConfirmPage';
import { AccountSetPasswordPage } from '@vehix/portal/features/auth/pages/AccountSetPasswordPage';

// Dashboard (landing page)
import AdminDashboardPage from '@vehix/shared/admin/pages/AdminDashboardPage';

// Setup section
import TenantSetupPage from '@vehix/shared/admin/pages/setup/TenantSetupPage';
import UserSetupPage from '@vehix/shared/admin/pages/setup/UserSetupPage';
import ZoneSetupPage from '@vehix/shared/admin/pages/setup/ZoneSetupPage';
import ZoneListPage from '@vehix/shared/admin/pages/setup/ZoneListPage';
import ConfigurationsPage from '@vehix/shared/admin/pages/setup/ConfigurationsPage';

// Workflow section — Workflow Management is eager (small),
// Pipeline pages are lazy (reactflow ~150KB + ~3,750 lines of UI).
import WorkflowManagementPage from '@vehix/shared/admin/pages/workflow/WorkflowManagementPage';
const PipelineDashboardPage = React.lazy(
  () => import('@vehix/shared/admin/pages/PipelineDashboardPage')
);
const PipelineDetailPage = React.lazy(() => import('@vehix/shared/admin/pages/PipelineDetailPage'));

// Monitoring section — placeholder pages, no heavy deps so eager-imported.
import DataQualityPage from '@vehix/shared/admin/pages/monitoring/DataQualityPage';
import SystemHealthPage from '@vehix/shared/admin/pages/monitoring/SystemHealthPage';

// Sales section — placeholder page, eager-imported.
import SalesDashboardPage from '@vehix/shared/admin/pages/sales/SalesDashboardPage';

// Analytics, Feedback, API Docs
const AnalyticsPage = React.lazy(() => import('@vehix/shared/admin/pages/AnalyticsPage'));
import FeedbackAdminPage from '@vehix/shared/admin/pages/FeedbackAdminPage';
import { ApiDocsPage } from '@vehix/shared/admin/pages/ApiDocsPage';

import { AdminLayout } from './components/AdminLayout';
import { AdminProtectedRoute } from './components/AdminProtectedRoute';

const PageLoader: React.FC = () => (
  <div className='flex items-center justify-center py-12'>
    <Loader2 className='h-8 w-8 animate-spin' />
    <span className='ml-2'>Loading...</span>
  </div>
);

// eslint-disable-next-line max-lines-per-function
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <EnhancedAuthProvider>
        <TenantProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              {/* Auth + onboarding flows (no admin gate) */}
              <Route path='/auth' element={<AuthPage variant='admin' />} />
              <Route path='/auth/callback' element={<AuthCallback />} />
              <Route path='/auth/set-password' element={<SetPasswordPage />} />
              <Route path='/auth/reset-password' element={<ResetPasswordPage />} />
              <Route path='/auth/reset-password/confirm' element={<ResetPasswordConfirmPage />} />
              <Route path='/auth/invite' element={<InvitePage />} />
              <Route path='/invite/confirm' element={<InviteConfirmPage />} />
              <Route path='/account/set-password' element={<AccountSetPasswordPage />} />

              {/* Default landing — Admin Dashboard with KPI widgets */}
              <Route
                path='/'
                element={
                  <AdminProtectedRoute page='dashboard'>
                    <AdminLayout>
                      <AdminDashboardPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />

              {/* Legacy redirects from the pre-restructure URL shape. */}
              <Route path='/control-center' element={<Navigate to='/setup/tenants' replace />} />
              <Route path='/pipelines' element={<LegacyPipelineListRedirect />} />
              <Route
                path='/pipelines/schedules'
                element={<Navigate to='/workflow/pipelines?tab=schedules' replace />}
              />
              <Route
                path='/pipelines/:id'
                element={<LegacyPipelineDetailRedirect />}
              />

              {/* Setup section */}
              <Route
                path='/setup/tenants'
                element={
                  <AdminProtectedRoute page='setup-tenants'>
                    <AdminLayout>
                      <TenantSetupPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />
              <Route
                path='/setup/users'
                element={
                  <AdminProtectedRoute page='setup-users'>
                    <AdminLayout>
                      <UserSetupPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />
              <Route
                path='/setup/zones'
                element={
                  <AdminProtectedRoute page='setup-zones'>
                    <AdminLayout>
                      <ZoneSetupPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />
              <Route
                path='/setup/zone-list'
                element={
                  <AdminProtectedRoute page='setup-zone-list'>
                    <AdminLayout>
                      <ZoneListPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />
              <Route
                path='/setup/configurations'
                element={
                  <AdminProtectedRoute page='setup-configurations'>
                    <AdminLayout>
                      <ConfigurationsPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />

              {/* Workflow section */}
              <Route
                path='/workflow/management'
                element={
                  <AdminProtectedRoute page='workflow-management'>
                    <AdminLayout>
                      <WorkflowManagementPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />
              <Route
                path='/workflow/pipelines'
                element={
                  <AdminProtectedRoute page='pipelines'>
                    <AdminLayout>
                      <Suspense fallback={<PageLoader />}>
                        <PipelineDashboardPage />
                      </Suspense>
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />
              {/* Schedules is a sub-tab of the Pipelines page, not a page of its
                  own — this used to render a standalone duplicate that nothing
                  navigated to. The route entry has to stay: without it the URL
                  falls through to '/workflow/pipelines/:id' below with
                  id === 'schedules', which fetches a pipeline named "schedules"
                  and renders an error page instead of redirecting. */}
              <Route
                path='/workflow/pipelines/schedules'
                element={<Navigate to='/workflow/pipelines?tab=schedules' replace />}
              />
              <Route
                path='/workflow/pipelines/:id'
                element={
                  <AdminProtectedRoute page='pipelines'>
                    <AdminLayout>
                      <Suspense fallback={<PageLoader />}>
                        <PipelineDetailPage />
                      </Suspense>
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />

              {/* Sales section */}
              <Route
                path='/sales/dashboard'
                element={
                  <AdminProtectedRoute page='sales-dashboard'>
                    <AdminLayout>
                      <SalesDashboardPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />

              {/* Monitoring section */}
              <Route
                path='/monitoring/data-quality'
                element={
                  <AdminProtectedRoute page='monitoring-data-quality'>
                    <AdminLayout>
                      <DataQualityPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />
              <Route
                path='/monitoring/system-health'
                element={
                  <AdminProtectedRoute page='monitoring-system-health'>
                    <AdminLayout>
                      <SystemHealthPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />

              {/* Analytics */}
              <Route
                path='/analytics'
                element={
                  <AdminProtectedRoute page='analytics'>
                    <AdminLayout>
                      <Suspense fallback={<PageLoader />}>
                        <AnalyticsPage />
                      </Suspense>
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />

              {/* Feedback */}
              <Route
                path='/feedback'
                element={
                  <AdminProtectedRoute page='feedback'>
                    <AdminLayout>
                      <FeedbackAdminPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />

              {/* API Docs — accessible via footer link, not sidebar */}
              <Route
                path='/api-docs'
                element={
                  <AdminProtectedRoute page='api-docs'>
                    <AdminLayout>
                      <ApiDocsPage />
                    </AdminLayout>
                  </AdminProtectedRoute>
                }
              />

              {/* Anything else → admin home */}
              <Route path='*' element={<Navigate to='/' replace />} />
            </Routes>
          </Router>
        </TenantProvider>
      </EnhancedAuthProvider>
    </ThemeProvider>
  );
};

export default App;
